import { createReducer, on, Action } from '@ngrx/store';

import * as fromActions from './application-settings.actions';
import { clearAllCache } from '../../../core/store/actions/offline.actions';

export const reducerKey = 'applicationSettings';

export interface CachSizeOption {
  id: number;
  name: string;
  active?: boolean;
}

export interface ApplicationSettings {
  cacheOptions: CachSizeOption[];
  cacheSize: number;
  offlineCacheNotificationEnabled: boolean;
  pushNotificationEnabled: boolean;
  offlineCacheSizeResponse?: CachSizeOption;
  wifiOnly: boolean;
  loading: boolean;
  loaded: boolean;
}

export const initialState: ApplicationSettings = {
  cacheOptions: [],
  cacheSize: 0,
  offlineCacheNotificationEnabled: false,
  pushNotificationEnabled: false,
  offlineCacheSizeResponse: <CachSizeOption>{},
  wifiOnly: false,
  loading: false,
  loaded: false,
};

const createApplicationSettingsReducer = createReducer(
  initialState,
  on(
    fromActions.loadCacheOptions,
    fromActions.setCacheOption,
    fromActions.setWifiOnly,
    fromActions.setOfflinePlayback,
    fromActions.clearCache,
    fromActions.loadApplicationSettings,
    (state: ApplicationSettings) => ({ ...state, loading: true, loaded: false }),
  ),
  on(
    fromActions.loadCacheOptionsFailed,
    fromActions.setCacheOptionFailed,
    fromActions.setWifiOnlyFailed,
    fromActions.setOfflinePlaybackFailed,
    fromActions.clearCacheFailed,
    fromActions.loadApplicationSettingsFailed,
    (state: ApplicationSettings) => ({ ...state, loading: false, loaded: false }),
  ),
  on(fromActions.loadCacheOptionsSuccess, (state: ApplicationSettings, action: any) => {
    const { cacheOptions } = action;
    return { ...state, cacheOptions, loading: false, loaded: true };
  }),
  on(fromActions.setCacheOptionSuccess, (state: any, action: any) => {
    const offlineCacheSizeResponse = { id: action.optionKey };
    return { ...state, offlineCacheSizeResponse, loading: false, loaded: true };
  }),
  on(
    fromActions.setWifiOnlySuccess,
    (state: ApplicationSettings, action: { wifiOnly: boolean }) => {
      const { wifiOnly } = action;
      return { ...state, wifiOnly, loading: false, loaded: true };
    },
  ),
  on(
    fromActions.setOfflinePlaybackSuccess,
    (state: ApplicationSettings, action: { isOfflineCacheEnabled: boolean }) => {
      const offlineCacheNotificationEnabled = action.isOfflineCacheEnabled;
      return { ...state, offlineCacheNotificationEnabled, loading: false, loaded: true };
    },
  ),
  on(
    fromActions.loadApplicationSettingsSuccess,
    (state: ApplicationSettings, action: { applicationSettings: ApplicationSettings }) => {
      const {
        offlineCacheNotificationEnabled,
        pushNotificationEnabled,
        offlineCacheSizeResponse,
        wifiOnly,
      } = action.applicationSettings;
      return {
        ...state,
        offlineCacheNotificationEnabled,
        pushNotificationEnabled,
        offlineCacheSizeResponse,
        wifiOnly,
        loading: false,
        loaded: true,
      };
    },
  ),
  on(
    fromActions.refreshCacheSizeSuccess,
    (state: ApplicationSettings, action: { cacheSize: number }) => {
      const { cacheSize } = action;
      return {
        ...state,
        cacheSize,
      };
    },
  ),
  on(clearAllCache, (state: ApplicationSettings) => ({
    ...state,
    cacheSize: 0,
  })),
);

export function applicationSettingsReducer(
  state: ApplicationSettings = initialState,
  action: Action,
): ApplicationSettings {
  return createApplicationSettingsReducer(state, action);
}
